import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { SelectDropdown, TextAreaInput } from "../../reusables/FormElements";

import { IComponentData, IComponentFormProps, ComponentField } from "../../../interfaces/forms";
import { ISelectDropdownOption } from "../../../interfaces/form_elements";
import { TLogicalOperator } from "../../../interfaces/types";

import { OPLogicalOperators } from "../../../data/options";

import useSaveComponent from "../../../hooks/components/useSaveComponent";
import useDeleteComponent from "../../../hooks/components/useDeleteComponent";

import { closeModal, openModal } from "../../../store/slices/modal";

const ComponentForm = (props: IComponentFormProps) => {
    const { componentData, selected, setSelected, setExpanded } = props;

    const dispatch = useDispatch();

    const requiredFields: ComponentField[] = ["method"];
    const selectFields: ComponentField[] = ["method"];

    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState<IComponentData>({
        id: null,
        program_id: null,
        description: { value: "", error: "" },
        method: { value: { value: "", label: "" }, error: "" },
    });

    const { mutate: saveComponent } = useSaveComponent();
    const { mutate: deleteComponent } = useDeleteComponent();

    useEffect(() => {
        if (componentData) {
            const method = OPLogicalOperators.find(method => method.value === componentData.method);

            setFormData({
                id: componentData.id > 0 ? componentData.id : null,
                program_id: componentData.program_id,
                description: { value: componentData.description ?? '', error: '' },
                method: { value: { value: method?.value ?? '', label: method?.label ?? '' }, error: '' }
            });
        } else {
            setFormData({
                id: null,
                program_id: null,
                description: { value: "", error: "" },
                method: { value: { value: "", label: "" }, error: "" },
            });
        };

        // eslint-disable-next-line
    }, [componentData]);

    const onChangeText = (name: ComponentField, value: string) => {
        setFormData({
            ...formData,
            [name]: { value, error: "" }
        });
    };

    const onChangeSelect = (name: ComponentField, option: ISelectDropdownOption) => {
        setFormData(prevData => {
            return {
                ...prevData,
                [name]: { value: option, error: "" }
            };
        });
    };

    const isDisabled = () => {
        const emptyFields = requiredFields.some(field => {
            let value = "";
            if (selectFields.includes(field)) {
                const dataValue = formData[field].value as ISelectDropdownOption;
                value = dataValue.value;
            } else {
                value = formData[field].value as string;
            };
            return !value?.trim()?.length;
        });

        const isDataSame =
            formData.description.value === (componentData?.description ?? '') &&
            formData.method.value.value === (componentData?.method ?? '');

        return emptyFields || isDataSame || isProcessing || !formData.program_id;
    };

    const onSave = () => {
        if (isDisabled() || !selected) return;

        // clear all errors
        setFormData(prevData => {
            const newData = { ...prevData };
            Object.keys(newData).forEach(key => {
                if (key === "id" || key === "program_id") return;
                newData[key as ComponentField].error = "";
            });
            return newData;
        });

        setIsProcessing(true);

        saveComponent({
            payload: {
                id: formData.id,
                program_id: formData.program_id as number,
                description: formData.description.value,
                method: formData.method.value.value as TLogicalOperator,
            },
            setIsProcessing,
            selected,
            setSelected,
        });
    };

    const onDelete = () => {
        if (!selected) return;

        if (!formData.id) {
            setSelected(null);
            return;
        };

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'CONFIRMATION',
                closeBtn: true,
                title: "Confirm delete component?",
                content: "This will also delete all associated functions. This action cannot be undone.",
                actionTxt: "Delete",
                action: () => {
                    setIsProcessing(true);

                    deleteComponent({
                        id: formData.id as number,
                        setIsProcessing,
                        setSelected,
                        setExpanded,
                    });

                    dispatch(closeModal('MESSAGE_MODAL'));
                },
                cancelTxt: "Cancel",
            }
        }))
    };

    return (
        <div className="immployer_admin__common_form">
            <div className="immployer_admin__common_form__inputs">
                <TextAreaInput
                    name="description"
                    max_length={200}
                    label="Component Description"
                    errorMsg={formData.description.error}
                    placeholder="Enter component description"
                    onChange={(value: string) => onChangeText("description", value)}
                    value={formData.description.value}
                />
                <SelectDropdown
                    name="method"
                    label="Select method"
                    required={true}
                    placeholder="-- select option --"
                    value={formData.method.value}
                    onChange={(option: ISelectDropdownOption) => onChangeSelect("method", option)}
                    errorMsg={formData.method.error}
                    options={OPLogicalOperators}
                />
            </div>
            <div className="immployer_admin__common_form__actions">
                <button
                    className={`immployer_admin__btn immployer_admin__btn__secondary ${isProcessing ? "immployer_admin__btn__secondary__disabled" : ""}`}
                    onClick={() => isProcessing ? null : onDelete()}
                >
                    {!formData.id ? 'Discard' : 'Delete'}
                </button>
                <button
                    className={`immployer_admin__btn immployer_admin__btn__primary ${isDisabled() ? "immployer_admin__btn__primary__disabled" : ""}`}
                    onClick={onSave}
                >
                    {
                        isProcessing
                            ? <i className='immployer_admin__btn_loader' />
                            : 'Save'
                    }
                </button>
            </div>
        </div>
    );
};

export default ComponentForm;